import * as React from 'react';
import { APP_MODE_STORAGE_KEY, getStoredAppMode, type AppMode } from '@/lib/app-mode';

export type { AppMode };

interface AppModeContextValue {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
}

const AppModeContext = React.createContext<AppModeContextValue | null>(null);

export function AppModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<AppMode>(getStoredAppMode);

  const setMode = React.useCallback((next: AppMode) => {
    setModeState(next);
    localStorage.setItem(APP_MODE_STORAGE_KEY, next);
  }, []);

  // The API client reads the mode from storage on every request, so another
  // tab switching it must move this tab's screen too, or the two disagree.
  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === APP_MODE_STORAGE_KEY) setModeState(getStoredAppMode());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AppModeContext.Provider value={{ mode, setMode }}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode(): AppModeContextValue {
  const ctx = React.useContext(AppModeContext);
  if (!ctx) throw new Error('useAppMode must be used within AppModeProvider');
  return ctx;
}

/**
 * Whether the app is in Working mode, for components that can also render
 * outside the provider (tests, bare pages). Working is the default mode, so
 * with no provider the answer is yes.
 */
export function useIsWorkingMode(): boolean {
  const ctx = React.useContext(AppModeContext);
  return ctx ? ctx.mode === 'working' : true;
}
